/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import { LoadingDots } from '@entrata/portals-common/lib/components';
import { ArrowRightSm, CircleAdd } from '@entrata/portals-common/lib/icons';
import styled from '@emotion/styled';
import { BLUE } from 'constants/theme';

const StyledButton = styled('button')`
  width: ${({ block }) => (block ? '100%' : 'auto')};
  height: 48px;
  border: solid 1px ${({ theme }) => theme.gray.medium};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.soehneBuch};
  font-size: 14px;
  padding: 0 24px;
  user-select: none;
  transition: all 300ms ease;
  border-radius: 24px;

  &:focus {
    outline: none;
  }

  &[disabled] {
    opacity: 0.5;
  }
`;

const PrimaryBtn = styled(StyledButton)`
  background-color: ${({ theme }) => theme.primary.base};
  border: none;
  color: ${({ theme }) => theme.text.dark};
  font-family: ${({ theme }) => theme.fonts.soehneBuch};
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;

  &:hover:not([disabled]),
  &:focus {
    cursor: pointer;
  }
`;

const CtaBtn = styled(PrimaryBtn)`
  height: 48px;
  font-size: 16px;

  svg {
    margin-left: 24px;
  }

  @media only screen and (max-width: 320px) {
    width: 100%;
    padding: 0 8px;
  }
`;

const SecondaryBtn = styled(StyledButton)`
  background: ${({ theme }) => theme.tertiary.medium};
  color: ${({ theme }) => theme.secondary.base};

  &:hover:not([disabled]),
  &:focus {
    border-color: ${({ theme }) => theme.secondary.base};
  }
`;

const TertiaryBtn = styled(StyledButton)`
  background: none;
  color: ${({ theme }) => theme.secondary.base};

  &:hover:not([disabled]),
  &:focus {
    border-color: ${({ theme }) => theme.secondary.base};
  }

  svg {
    margin-right: 8px;
  }
`;

const RectangleBtn = styled(StyledButton)`
  width: 186px;
  height: 37px;
  background: ${({ theme }) => theme.tertiary.medium};
  color: ${({ theme }) => theme.tertiary.light};
  border-radius: 0;
  justify-content: left;
  padding: 0 16px;
  position: relative;

  &:hover:not([disabled]),
  &:focus {
    border-color: ${({ theme }) => theme.secondary.base};
  }
  svg {
    position: absolute;
    right: 8px;
  }
`;

const appearanceMap = {
  cta: CtaBtn,
  primary: PrimaryBtn,
  secondary: SecondaryBtn,
  tertiary: TertiaryBtn,
  rectangle: RectangleBtn,
};

const Btn = ({ appearance, children, disabled, Icon, onClick, loading, size, ...other }) => {
  const Component = appearanceMap[appearance] || StyledButton;
  return (
    <Component disabled={loading || disabled} onClick={!disabled ? onClick : null} {...other}>
      {!loading && appearance === 'tertiary' && typeof Icon === 'function' && <Icon size={size} />}
      {loading ? <LoadingDots width='32px' /> : children}
      {!loading && appearance === 'cta' && <ArrowRightSm size={12} />}
      {appearance === 'rectangle' && <CircleAdd size={14} color={`${BLUE}`} />}
    </Component>
  );
};

Btn.propTypes = {
  appearance: 'primary',
}

Btn.propTypes = {
  /** Determines the style of the button */
  appearance: PropTypes.oneOf(['cta', 'primary', 'secondary', 'tertiary', 'rectangle']),
  /**  Determines whether the button fills the width of it's container or not. */
  block: PropTypes.bool,
  /** Text node or Icon to label the button */
  children: PropTypes.any,
  /** Determines if the button is disabled or not. */
  disabled: PropTypes.bool,
  /** Icon component (for tertiary buttons only) */
  Icon: PropTypes.func,
  /** Size for Icon */
  size: PropTypes.number,
  /** If loading, the button will show loading dots */
  loading: PropTypes.bool,
  /** Function that is called when Button is clicked */
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

Btn.defaultProps = {
  block: false,
  disabled: false,
  type: 'button',
  size: 14,
};

export default Btn;
